import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiErrorText, massIncidentsApi, type MassIncidentSummary } from '@/shared/api';
import { MassIncidentIcon } from '@/shared/config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  ErrorState,
  Input,
  Scroller,
  Skeleton,
  toast,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { pluralRu } from '@/shared/lib/plural';
import { incidentMeta, rankIncidents } from '../model/logic';
import { refreshAfterIncidentChange, type PickerRequest } from '../model/store';
import styles from './MassIncidents.module.css';

type Option = { kind: 'existing'; incident: MassIncidentSummary } | { kind: 'new'; title: string };

export const MassIncidentPicker: React.FC<{ request: PickerRequest; onClose: () => void }> = ({
  request,
  onClose,
}) => {
  const [incidents, setIncidents] = useState<MassIncidentSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [sending, setSending] = useState(false);
  const count = request.ticketIds.length;

  const load = () => {
    setLoadError(null);
    massIncidentsApi
      .list('active')
      .then((res) => setIncidents(Array.isArray(res.data) ? res.data : []))
      .catch((err: unknown) => setLoadError(apiErrorText(err, 'Не удалось загрузить сбои')));
  };
  useEffect(load, []);

  const options = useMemo<Option[]>(() => {
    const ranked = rankIncidents(incidents ?? [], request, query);
    return [
      ...ranked.map((incident) => ({ kind: 'existing' as const, incident })),
      { kind: 'new' as const, title: query.trim() },
    ];
  }, [incidents, request, query]);

  useEffect(() => setCursor(0), [query]);

  const pick = async (option: Option) => {
    if (sending) return;
    setSending(true);
    try {
      const res = await massIncidentsApi.addTickets(
        request.ticketIds,
        option.kind === 'existing' ? { incidentId: option.incident.id } : { title: option.title },
      );
      const { incident, added, skipped } = res.data;
      const note = skipped.length > 0 ? `, пропущено ${skipped.length}: ${skipped[0].reason.toLowerCase()}` : '';
      toast.success(
        count === 1 && skipped.length === 0
          ? `Заявка в сбое «${incident.title}»`
          : `В сбой «${incident.title}» — ${added.length} ${pluralRu(added.length, 'заявка', 'заявки', 'заявок')}${note}`,
      );
      refreshAfterIncidentChange(incident.id, added);
      request.onDone?.(res.data);
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось добавить в сбой'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const option = options[cursor];
      if (option) void pick(option);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={styles.picker}>
        <DialogTitle>В массовый сбой</DialogTitle>
        <DialogDescription>
          {count === 1
            ? 'Выберите действующий сбой или заведите новый.'
            : `${count} ${pluralRu(count, 'заявка', 'заявки', 'заявок')}: выберите действующий сбой или заведите новый.`}
        </DialogDescription>
        <Input
          autoFocus
          aria-label="Найти сбой или назвать новый"
          placeholder="Найти сбой или назвать новый"
          value={query}
          maxLength={500}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-controls="mass-incident-options"
          aria-activedescendant={`mass-incident-option-${cursor}`}
        />
        {loadError ? (
          <ErrorState message={loadError} onRetry={load} />
        ) : (
          <Scroller className={styles.optionsScroller} viewportClassName={styles.options}>
            <div id="mass-incident-options" role="listbox" aria-label="Сбои" className={styles.optionList}>
              {incidents === null
                ? [0, 1].map((i) => (
                    <div key={i} className={styles.option}>
                      <Skeleton width="55%" height="0.875rem" />
                      <Skeleton width="35%" height="0.75rem" />
                    </div>
                  ))
                : options.map((o, i) => (
                    <button
                      key={o.kind === 'existing' ? o.incident.id : '__new'}
                      id={`mass-incident-option-${i}`}
                      type="button"
                      role="option"
                      aria-selected={i === cursor}
                      disabled={sending}
                      className={cn(styles.option, i === cursor && styles.optionActive)}
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => void pick(o)}
                    >
                      <span className={cn(styles.optionIcon, o.kind === 'new' && styles.optionIconNew)}>
                        {o.kind === 'new' ? <Plus size={14} aria-hidden /> : <MassIncidentIcon size={14} aria-hidden />}
                      </span>
                      <span className={styles.optionText}>
                        <span className={styles.optionTitle}>
                          {o.kind === 'existing'
                            ? o.incident.title
                            : o.title
                              ? `Новый сбой «${o.title}»`
                              : 'Новый сбой'}
                        </span>
                        <span className={styles.optionMeta}>
                          {o.kind === 'existing'
                            ? incidentMeta(o.incident)
                            : o.title
                              ? 'Название видят заявители'
                              : 'Название — по категории заявки'}
                        </span>
                      </span>
                    </button>
                  ))}
            </div>
          </Scroller>
        )}
      </DialogContent>
    </Dialog>
  );
};
